import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '@streamster/shared/screens/auth/auth.utils';
import { useSupportChatIdentityQuery } from '@streamster/shared/api/api.portal';
import { isWhiteLabelMode } from '@streamster/shared/utils';
import useSupportChatContext from './useSupportChatContext';

// Streamster support chat — the sy-support-chat widget (project "streamster"),
// replacing the old Chaport embed. It recognizes the signed-in Streamster user
// from a signed identity token minted by account.streamster.io
// (/api/ai-chat/identity) and handed straight to the widget, so the assistant
// greets them and support tickets carry their account email without asking.
//
// The widget's own /api/ai-chat/identity lookup is cookie-based and same-origin;
// it can't be used here because the web app authenticates with a bearer token on
// a different origin (account.streamster.io). So we fetch the token ourselves
// (bearer-authenticated) and pass it in via data-identity-token / -email.

// Where the widget bundle + its chat API are served from. Overridable at runtime
// (white-label / staging) via window.stcfg.supportChatUrl.
const SUPPORT_CHAT_URL =
  (typeof window !== 'undefined' && (window as any)?.stcfg?.supportChatUrl) ||
  'https://support.streamster.io';

// Matches streamsterConfig.primaryColor in sy-support-chat, so the launcher is on
// brand from the first frame instead of flashing the widget's generic default.
const PRIMARY_COLOR = '#0891B2';

// How long to wait for the identity call before loading the chat without it.
const IDENTITY_TIMEOUT_MS = 4000;

const StreamsterSupportChat = () => {
  const injectedRef = useRef(false);
  const { isAuth } = useContext(AuthContext);
  const isWhitelabel = isWhiteLabelMode();
  // Only authenticated users get recognized; the query is skipped otherwise (it
  // needs the bearer token). data resolves once, with token=null if the server
  // has no shared secret — in which case the widget still loads, anonymously.
  const { data: identity } = useSupportChatIdentityQuery({ skip: !isAuth || isWhitelabel });

  // Keeps the assistant supplied with the live channel/encoder state, so it can
  // diagnose streaming problems rather than interrogate the user about their setup.
  useSupportChatContext();

  useEffect(() => {
    if (injectedRef.current || isWhitelabel || !isAuth) {
      return;
    }

    const inject = () => {
      if (injectedRef.current) {
        return;
      }
      injectedRef.current = true;

      const script = document.createElement('script');
      script.src = `${SUPPORT_CHAT_URL}/widget.js`;
      script.async = true;
      script.dataset.projectId = 'streamster';
      script.dataset.apiUrl = SUPPORT_CHAT_URL;
      script.dataset.platform = 'web';
      script.dataset.primaryColor = PRIMARY_COLOR;
      if (identity?.token) {
        script.dataset.identityToken = identity.token;
      }
      if (identity?.email) {
        script.dataset.identityEmail = identity.email;
      }
      document.body.appendChild(script);
    };

    if (identity) {
      inject();
      return;
    }

    // Identity is an enhancement, not a prerequisite: if the endpoint is missing,
    // slow or erroring, still show the chat (unrecognized) rather than no chat at
    // all — a support widget that disappears exactly when the backend is unhappy
    // is the worst possible failure mode.
    const timer = setTimeout(inject, IDENTITY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isAuth, identity, isWhitelabel]);

  // The token above is baked into the <script> tag once, at first injection. In
  // a single-page app the user can sign out and back in as someone else without
  // that ever running again, leaving the widget authenticated as the previous
  // account — it would answer questions about their plan and balance. Pushing
  // every identity change keeps the widget in step; the widget starts a fresh
  // conversation when the subject actually changes.
  useEffect(() => {
    const setIdentity = (window as any)?.SupportChat?.setIdentity;
    if (typeof setIdentity !== 'function') {
      return;
    }

    setIdentity(identity?.token ?? null, identity?.email ?? null);
  }, [identity?.token, identity?.email]);

  // Signing out entirely: drop the identity so the widget stops speaking for the
  // account that just left. The query is skipped while unauthenticated, so the
  // effect above would otherwise keep the last value it saw.
  useEffect(() => {
    if (isAuth) {
      return;
    }

    const setIdentity = (window as any)?.SupportChat?.setIdentity;
    if (typeof setIdentity === 'function') {
      setIdentity(null, null);
    }
  }, [isAuth]);

  return null;
};

export default StreamsterSupportChat;
